/**
 * dor-home2-motion.js / EXPERIMENT 2 / one scroll-motion engine, sitewide
 * ---------------------------------------------------------------------------
 * Adapted from the approved Experiment 1 engine (dor-x-motion.js): same arming
 * gate, same safety nets, same discipline. Loaded deferred AFTER gsap.min.js +
 * ScrollTrigger.min.js. Arms html.dor-m-on ONLY when:
 *   1. the theme's motion gate passed (html.dor-anim-on: IntersectionObserver
 *      supported AND prefers-reduced-motion off), and
 *   2. gsap + ScrollTrigger actually loaded.
 * Hidden states are applied by THIS script (or CSS scoped under .dor-m-on),
 * so the unarmed page is complete, static, fully visible: reduced-motion
 * fallback = first frame by construction. Everything is scroll-triggered,
 * transforms/opacity only.
 *
 * THE SIGNATURE: every .dor-squiggle--accent inside <main> draws itself in
 * on scroll entrance (Vito's promoted detail). One implementation, sitewide.
 */
(function () {
  'use strict';

  function init() {
    var H = document.documentElement;
    if (!H.classList.contains('dor-anim-on')) return;
    if (!window.gsap || !window.ScrollTrigger) return;
    if (H.dataset.dorMotion) return;
    H.dataset.dorMotion = '1';
    var g = window.gsap;
    g.registerPlugin(window.ScrollTrigger);
    H.classList.add('dor-m-on');

    /* --- 1. SIGNATURE SQUIGGLE DRAW-IN, sitewide (main content only; the
       header "Shop" squiggle is chrome and stays static). A squiggle already
       in view at arm time draws immediately, which is the page's own
       entrance. */
    document.querySelectorAll('main .dor-squiggle--accent').forEach(function (sq) {
      g.set(sq, { scaleX: 0, transformOrigin: 'left center' });
      g.to(sq, {
        scaleX: 1, duration: 0.8, ease: 'power2.out',
        scrollTrigger: { trigger: sq.closest('h1,h2,h3,p,section') || sq, start: 'top 82%' }
      });
    });

    /* --- 2. Generic entrance choreography: any [data-mgroup] staggers its
       [data-mreveal] children up as the group enters. */
    document.querySelectorAll('[data-mgroup]').forEach(function (group) {
      var items = group.querySelectorAll('[data-mreveal]');
      if (!items.length) return;
      g.set(items, { autoAlpha: 0, y: 26 });
      g.to(items, {
        autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.11,
        scrollTrigger: { trigger: group, start: 'top 76%' }
      });
    });

    /* --- 3. HERO. Puglia stage: the scene photo scrolls slightly slower
       than the page (scrub-linked translate, 12% CSS head-room above the
       section keeps it seamless). Ember stage: the fall, unchanged. */
    var hero = document.querySelector('.dor-home2-hero');
    if (hero) {
      var scene = hero.querySelector('[data-mscene]');
      if (scene) {
        g.to(scene, {
          yPercent: 8, ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.4 }
        });
      }
      var layers = hero.querySelectorAll('[data-mfall]');
      if (layers.length) {
        var heroTl = g.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.5 }
        });
        layers.forEach(function (el) {
          heroTl.to(el, {
            yPercent: parseFloat(el.dataset.mfall) || 30,
            rotation: parseFloat(el.dataset.mrot) || 0
          }, 0);
        });
        var glow = hero.querySelector('[data-mglow]');
        if (glow) heroTl.to(glow, { opacity: 0.95, scale: 1.1 }, 0);
      }
    }

    /* --- 4. CLAIMS, two implementations of one job (the photo is the
       infographic). Desktop: chips pop in anchored to the product, ties
       draw first. Mobile: pinned scrub, the claims assemble around the
       product as you scroll (runway CSS is scoped mobile + .dor-m-on). */
    var claims = document.querySelector('.dor-claims');
    if (claims) {
      var mm = g.matchMedia();
      mm.add('(min-width: 750px)', function () {
        var chips = claims.querySelectorAll('[data-mchip]');
        var ties = claims.querySelectorAll('[data-mtie]');
        if (!chips.length) return;
        g.set(chips, { autoAlpha: 0, scale: 0.62, y: 12 });
        g.set(ties, { scaleX: 0 });
        var ctl = g.timeline({ scrollTrigger: { trigger: claims, start: 'top 68%' } });
        ctl.to(ties, { scaleX: 1, duration: 0.45, ease: 'power2.out', stagger: 0.08 }, 0.05);
        ctl.to(chips, { autoAlpha: 1, scale: 1, y: 0, duration: 0.55, ease: 'back.out(1.7)', stagger: 0.08 }, 0);
      });
      mm.add('(max-width: 749px)', function () {
        /* POLISH E: the pinned 220vh runway is gone (it clipped the grown
           stack inside a 100svh overflow-hidden frame and held the scroll
           hostage). Natural flow now: each chip / the CTA arrives promptly
           as IT enters the viewport — one quick rise, entrance-once. */
        var steps = claims.querySelectorAll('[data-mcrave]');
        if (!steps.length) return;
        g.set(steps, { autoAlpha: 0, y: 18 });
        steps.forEach(function (el, i) {
          g.to(el, {
            autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', delay: (i % 2) * 0.07,
            scrollTrigger: { trigger: el, start: 'top 92%', once: true }
          });
        });
      });
    }

    /* --- 5. BAKERY: the film frame scales 0.94 -> 1, scrubbed. */
    var bak = document.querySelector('.dor-home2-bakery__frame');
    if (bak) {
      g.set(bak, { scale: 0.94, transformOrigin: '50% 70%' });
      g.to(bak, {
        scale: 1, ease: 'none',
        scrollTrigger: { trigger: bak.closest('section'), start: 'top 82%', end: 'top 28%', scrub: 0.5 }
      });
    }

    /* --- 6. Photo tilt-to-straighten (story frame, affiliates photo):
       a print put down on the table, settling as you scroll. */
    document.querySelectorAll('[data-mtilt]').forEach(function (ph) {
      g.set(ph, { rotation: -2.5, y: 18, scale: 0.965, transformOrigin: '50% 60%' });
      g.to(ph, {
        rotation: 0, y: 0, scale: 1, ease: 'none',
        scrollTrigger: { trigger: ph, start: 'top 90%', end: 'top 42%', scrub: 0.5 }
      });
    });

    /* --- 7. ANIMA PUGLIA warmth entrances (round 3): etchings sketch
       themselves in (a soft directional mask wipe finishing the drawing),
       walls resolve like light reaching stone. The gestures live in CSS
       (dorellini-brand.css, [data-ap-enter] block); this only stamps
       .is-ap-in once on scroll entrance. Hidden states are scoped under
       html.dor-m-on, so the unarmed / reduced-motion page is complete
       and static by construction. */
    document.querySelectorAll('[data-ap-enter]').forEach(function (el) {
      window.ScrollTrigger.create({
        trigger: el, start: 'top 86%', once: true,
        onEnter: function () { el.classList.add('is-ap-in'); }
      });
    });

    /* --- Safety nets (house philosophy: content can never stay hidden). */
    setTimeout(function () { window.ScrollTrigger.refresh(); }, 1200);
    setTimeout(function () {
      document.querySelectorAll('[data-mreveal], [data-mchip], [data-mcrave]').forEach(function (el) {
        var st = window.getComputedStyle(el);
        if (st.opacity === '0' && el.getBoundingClientRect().top < window.innerHeight) {
          el.style.opacity = '1';
          el.style.transform = 'none';
          el.style.visibility = 'visible';
        }
      });
      /* AP net: any warmth element already inside the viewport whose
         trigger never fired arrives now. */
      document.querySelectorAll('[data-ap-enter]:not(.is-ap-in)').forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('is-ap-in');
        }
      });
    }, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () {
    if (document.documentElement.dataset.dorMotion && window.ScrollTrigger) {
      window.ScrollTrigger.refresh();
    } else {
      init();
    }
  });
})();
